import React, { useEffect, useMemo, useState } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { Separator, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import TranscriptAlertBanner from './components/TranscriptAlertBanner'
import BlankState from '../ui/BlankState'
import SignInBlankState from '../ui/SignInBlankState'
import AddNamesPanel from '../../../fullText/components/addNamesPanel/AddNamesPanel'
import { ADD_NAME_PANEL_REFERRERS } from '../../../fullText/components/addNamesPanel/utils/utils'
import HouseholdList from '../../../../components/generic/householdList/HouseholdList'
import useHouseholdList from '../../../../components/generic/householdList/hooks/useHouseholdsList'

/**
 * Main panel for the transcript and names indexing interface. Displays persons grouped by household,
 * provides filtering, transcript banner, and manages the full text overlay.
 *
 * Features:
 * - Household list with filtering by person name
 * - Transcript alert banner when transcripts are available
 * - Full text overlay controlled by query params
 * - Create household card for adding new households
 * - Conditional rendering based on sign-in status and blank state
 * - Change history display
 * - Add names panel for creating new persons with household tracking disabled
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.loading=false] - External loading state indicator
 * @returns {JSX.Element}
 */
const TranscriptAndNamesPanel = ({ loading = false, additionalHeaderContent }) => {
  const [t] = useTranslation()
  const { signedIn } = useUser()
  const [filterValue, setFilterValue] = useState('')
  const [selectedRecordId, setSelectedRecordId] = useState(null)
  const { query, setQuery } = useQueryParams()
  const { view } = query || {}
  const { householdListProps, showBlankState, showChangeHistory } = useHouseholdList({
    loading,
    filterValue,
  })
  const fullTextOverlay = useOverlay({ initiallyOpen: view === 'fullText' })

  const handleViewTranscript = () => setQuery({ view: 'fullText' })

  // The single source of truth here is the query param
  useEffect(() => {
    if (view === 'fullText') {
      fullTextOverlay.open()
    } else {
      fullTextOverlay.close()
    }
  }, [view, fullTextOverlay])

  const description = useMemo(() => {
    if (view === 'fullText') {
      return t(
        'transcript-and-names-panel.fulltext-persons-list-description',
        'Add or edit people mentioned in the transcript. Or click a name to attach this source to Family Tree.'
      )
    }
    return t(
      'transcript-and-names-panel.index-persons-list-description',
      'Add or edit people you see in the document. Or click a name to attach this source to Family Tree.'
    )
  }, [view, t])

  return (
    <>
      {signedIn ? (
        <>
          {/* Transcript Alert Banner */}
          <TranscriptAlertBanner handleViewTranscript={handleViewTranscript} fullTextOverlay={fullTextOverlay} />

          {additionalHeaderContent && (
            <>
              <Separator size="xs" />
              {additionalHeaderContent}
            </>
          )}

          {showBlankState ? (
            <BlankState />
          ) : (
            <HouseholdList
              description={description}
              setSelectedRecordId={setSelectedRecordId}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              {...householdListProps}
            />
          )}
        </>
      ) : (
        <>
          {showBlankState ? (
            <SignInBlankState />
          ) : (
            <HouseholdList
              setSelectedRecordId={setSelectedRecordId}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              {...householdListProps}
            />
          )}
        </>
      )}

      {/* Change History */}
      {showChangeHistory && <ShowChangeHistory showDivider={false} />}

      <AddNamesPanel
        referrer={ADD_NAME_PANEL_REFERRERS.BLANK_STATE}
        recordId={selectedRecordId}
        setSelectedRecordId={setSelectedRecordId}
      />
    </>
  )
}

export default TranscriptAndNamesPanel
